/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IoMdAirplane } from "react-icons/io";
import { RiShipLine, RiTruckLine } from "react-icons/ri";
import * as yup from "yup";

import { Destination } from "../../../../../../../../models/destination";
import { Status } from "../../../../../../../../util/enums";
import Input from "../../../../../../../../components/Form/Input";
import { ListColor, OPTIONS_COLORS } from "../../../../../../../../components/Generic/ListColor";
import Modal from "../../../../../../../../components/Generic/Modal";
import useStyles from "./styles";

interface Props {
  destination?: Destination;
  open: boolean;
  isLoading?: boolean;
  hasError?: boolean;
  isOnViewMode?: boolean;
  closeModal: () => void;
  submit: (destination: Destination) => void;
}

const DestinationModal = ({
  destination,
  open,
  isLoading,
  hasError,
  isOnViewMode,
  closeModal,
  submit,
}: Props) => {
  const { t: translate } = useTranslation();

  const [selectedDestination, setSelectedDestination] = useState<Destination>({
    name: "",
    color: OPTIONS_COLORS[OPTIONS_COLORS.length - 1],
  });
  const [status, setStatus] = useState<string>(Status.INACTIVE);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const classes = useStyles(selectedDestination?.color || "")();

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    code: yup.string().nullable(),
    name: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    color: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("string-check", translate("colorValueMustStartWithAndCannotBeAWhiteColor"), (color) =>
        color && color.length > 0 ? color.includes("#") && !color.includes("#fff") : false
      ),
    icon: yup.string().nullable(),
  });

  const handleValidateSuccess = (destination?: Destination): boolean => {
    schema.validateSync(
      {
        name: destination?.name,
        color: destination?.color,
        icon: destination?.icon,
      },
      { abortEarly: false }
    );
    setErrors([]);

    return true;
  };

  const handleValidateError = (error: any): boolean => {
    if (error instanceof yup.ValidationError) setErrors(error.inner);

    return false;
  };

  const isValid = (destination?: Destination) => {
    try {
      return handleValidateSuccess(destination);
    } catch (error) {
      return handleValidateError(error);
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (isValid(selectedDestination)) {
      if (selectedDestination) submit({ ...selectedDestination, status });
    }
  };

  useEffect(() => {
    if (destination) {
      setSelectedDestination(destination);
      setStatus(destination.status || Status.INACTIVE);
    }
  }, [destination]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      closeModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      isValid(selectedDestination);
    }
  }, [selectedDestination, hasFormBeenSubmitted]);

  return (
    <Modal
      data-cy="destinationModal"
      open={open}
      handleClose={closeModal}
      title={
        destination
          ? isOnViewMode
            ? translate("viewDestination")
            : translate("editDestination")
          : translate("createDestination")
      }
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => {
          closeModal();
        },
        loading: isLoading,
        textSave: destination ? translate("update") : translate("create"),
        dataCySave: "submitButton",
        onSave: isOnViewMode ? undefined : () => handleSubmit(),
      }}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        label: translate("destinationStatus"),
        value: status,
        dataCy: "selectStatus",
        disabled: isOnViewMode,
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatus(value),
      }}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" alignItems="center" spacing={2}>
          <Grid item md={6} lg={6}>
            <Input
              name="code"
              label={translate("code")}
              placeholder={translate("code")}
              disabled={isOnViewMode}
              value={selectedDestination?.code}
              error={!!getErrorByField("code")}
              errorMessage={getErrorByField("code")}
              onChange={(event) =>
                setSelectedDestination((state) =>
                  state
                    ? { ...state, code: event.target.value }
                    : ({ code: event.target.value } as Destination)
                )
              }
            />
          </Grid>
          <Grid
            item
            container
            md={selectedDestination?.name ? 9 : 12}
            lg={selectedDestination?.name ? 9 : 12}
            alignItems="flex-start"
          >
            <Grid item md={12} lg={12}>
              <Input
                name="name"
                label={translate("name")}
                placeholder={translate("name")}
                disabled={isOnViewMode}
                error={!!getErrorByField("name")}
                errorMessage={getErrorByField("name")}
                value={selectedDestination?.name}
                onChange={(event) =>
                  setSelectedDestination((state) =>
                    state
                      ? { ...state, name: event.target.value }
                      : ({ name: event.target.value } as Destination)
                  )
                }
              />
            </Grid>

            {/* TODO: verify how to get an icon */}
            <Grid item md={6} lg={6} style={{ display: "none" }}>
              <Input
                name="icon"
                label={translate("icon")}
                placeholder={translate("icon")}
                disabled={isOnViewMode}
                error={!!getErrorByField("icon")}
                errorMessage={getErrorByField("icon")}
                value={selectedDestination?.icon}
                onChange={(event) =>
                  setSelectedDestination((state) =>
                    state
                      ? { ...state, icon: event.target.value }
                      : ({ icon: event.target.value } as Destination)
                  )
                }
              />
            </Grid>
          </Grid>
          <Grid
            item
            container
            md={12}
            lg={12}
            alignItems="flex-start"
            spacing={1}
            direction="column"
          >
            <span className={classes.label}>{translate("color")}</span>

            <ListColor
              error={!!getErrorByField("color")}
              errorMessage={getErrorByField("color")}
              colorSelected={selectedDestination?.color || ""}
              onChangeColor={(color) =>
                setSelectedDestination((state) =>
                  state ? { ...state, color } : ({ color } as Destination)
                )
              }
            />
          </Grid>
          <Grid item container direction="column" md={3} lg={3} spacing={2}>
            {selectedDestination?.name && selectedDestination?.name.length > 0 && (
              <>
                <Grid item>
                  <span className={classes.label}>{translate("preview")}</span>
                </Grid>
                <Grid item>
                  <span className={classes.spanExample}>
                    <IoMdAirplane size={16} className="mr-2" />
                    {selectedDestination?.name}
                  </span>
                </Grid>
                <Grid item>
                  <span className={classes.spanExample}>
                    <RiShipLine size={16} className="mr-2" />
                    {selectedDestination?.name}
                  </span>
                </Grid>
                <Grid item>
                  <span className={classes.spanExample}>
                    <RiTruckLine size={16} className="mr-2" />
                    {selectedDestination?.name}
                  </span>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default DestinationModal;
